import { useState } from 'react';

import { Button } from '@/components/ui/Button';
import { Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/Input';
import { YStack } from '@/components/ui/layout/layout';
import { SearchableDropdown } from '@/components/ui/SearchableDropdown';

import { teamApis } from '../team.query';
import { MemberStatusEnum, type TeamMember } from '../team.types';

const CONFIRM_PHRASE = 'transfer ownership';

interface TransferOwnershipModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  teamId: string;
  members: TeamMember[];
  currentUserEmail: string | undefined;
}

export function TransferOwnershipModal({
  open,
  onOpenChange,
  teamId,
  members,
  currentUserEmail,
}: TransferOwnershipModalProps) {
  const [target, setTarget] = useState<TeamMember | null>(null);
  const [confirmText, setConfirmText] = useState('');
  const [transferring, setTransferring] = useState(false);

  const candidates = members.filter(
    (m) =>
      m.status === MemberStatusEnum.enum.active && m.email !== currentUserEmail
  );
  const canTransfer =
    !!target &&
    confirmText.trim().toLowerCase() === CONFIRM_PHRASE &&
    !transferring;

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      setTarget(null);
      setConfirmText('');
    }
    onOpenChange(next);
  };

  const handleTransfer = async () => {
    if (!target || !canTransfer) return;
    setTransferring(true);
    try {
      await teamApis.transferOwnership(teamId, target.id);
      handleOpenChange(false);
    } finally {
      setTransferring(false);
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={handleOpenChange}>
      <Dialog.Content className="max-w-sm">
        <Dialog.Header>
          <Dialog.Title>Transfer ownership</Dialog.Title>
          <Dialog.Description>
            You will become an admin. Only the new owner can transfer ownership
            back.
          </Dialog.Description>
        </Dialog.Header>
        <YStack className="gap-3">
          <SearchableDropdown
            options={candidates.map((m) => ({
              value: m.email,
              label: m.name || m.email,
              description: m.name ? m.email : undefined,
            }))}
            value={target?.email}
            placeholder="Choose a member"
            searchPlaceholder="Search members"
            onValueChange={(email) =>
              setTarget(candidates.find((m) => m.email === email) ?? null)
            }
            triggerClassName="text-xs bg-secondary hover:bg-secondary-hover"
            contentClassName="w-[var(--radix-dropdown-menu-trigger-width)] max-w-none"
            disabled={transferring}
            emptyResult="No members found"
          />
          {target && (
            <Input
              placeholder={`Type '${CONFIRM_PHRASE}' to confirm`}
              value={confirmText}
              disabled={transferring}
              onChange={(e) => setConfirmText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleTransfer();
              }}
            />
          )}
        </YStack>
        <Dialog.Footer>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => handleOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            size="sm"
            disabled={!canTransfer}
            isLoading={transferring}
            onClick={handleTransfer}
          >
            Transfer
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog.Root>
  );
}
